import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Film, Star, Eye, Plus, Trash2, Wallet, ExternalLink, AlertCircle, CheckCircle2, RotateCcw, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";
import FestivalCredentialCard from "../components/FestivalCredentialCard";

export default function FilmmakerDashboard() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [laurels, setLaurels] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [connect, setConnect] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feeBusy, setFeeBusy] = useState(null);  // submission_id being paid
  const [sp, setSp] = useSearchParams();
  const [subtitleLangs, setSubtitleLangs] = useState([]);
  const [translateBusy, setTranslateBusy] = useState(null); // submission_id being translated

  const load = () => {
    api.get("/festival/submissions/mine").then((r) => setSubs(r.data || [])).catch(() => setSubs([]));
    api.get("/festival/my-laurels").then((r) => setLaurels(r.data?.laurels || [])).catch(() => setLaurels([]));
    api.get("/festival/earnings").then((r) => setEarnings(r.data)).catch(() => setEarnings(null));
    api.get("/festival/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect(null));
  };
  useEffect(() => { load(); }, []);

  // Load supported translation languages once
  useEffect(() => {
    api.get("/festival/subtitle-languages").then((r) => setSubtitleLangs(r.data || [])).catch(() => setSubtitleLangs([]));
  }, []);

  const translateSubtitles = async (submission_id, target) => {
    if (!target) return;
    setTranslateBusy(submission_id);
    try {
      const resp = await api.post(
        `/festival/submissions/${submission_id}/subtitles.srt/translate`,
        { target },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/x-subrip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${submission_id}.${target}.srt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const langName = subtitleLangs.find((l) => l.code === target)?.name || target;
      toast.success(`Translated to ${langName}. Download starting…`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Translation failed";
      toast.error(typeof msg === "string" ? msg : "Translation failed");
    } finally {
      setTranslateBusy(null);
    }
  };

  // Post-checkout: poll fee status if Stripe sent us back with a fee_session_id
  useEffect(() => {
    const sid = sp.get("fee_session_id");
    if (!sid) return;
    // Find any pending_payment submission and poll its status
    let attempts = 0;
    let timer = null;
    const poll = async () => {
      attempts++;
      try {
        const { data: rows } = await api.get("/festival/submissions/mine");
        const pending = (rows || []).find((s) => s.submission_fee_session_id === sid);
        if (pending) {
          const { data: st } = await api.get(`/festival/submissions/${pending.submission_id}/fee-status`);
          if (st.submission_fee_paid || st.status === "approved") {
            toast.success("Submission fee paid — your film is now live!");
            sp.delete("fee_session_id");
            setSp(sp, { replace: true });
            load();
            return;
          }
        }
      } catch (e) {
        console.warn("[filmmaker] fee session poll failed", e?.response?.status);
      }
      if (attempts < 10) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payFee = async (s) => {
    setFeeBusy(s.submission_id);
    try {
      const { data } = await api.post(
        `/festival/submissions/${s.submission_id}/fee-checkout`,
        { origin_url: window.location.origin }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setFeeBusy(null);
    }
  };

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/festival/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/festival/connect/dashboard-link");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open dashboard");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this submission permanently?")) return;
    try { await api.delete(`/festival/submissions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const totalVotes = subs.reduce((s, x) => s + (x.total_votes || 0), 0);
  const totalViews = subs.reduce((s, x) => s + (x.view_count || 0), 0);
  const avgRating = subs.length ? subs.reduce((s, x) => s + (x.avg_rating || 0), 0) / subs.length : 0;

  if (!user) return <div className="min-h-screen bg-[#0A0A0A]"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-[1300px] mx-auto pb-20">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-black">Filmmaker dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">Track your submissions, audience engagement, and votes.</p>
          </div>
          <Link data-testid="new-submission-cta" to="/festival/submit" className="btn-red px-5 py-2.5 rounded font-semibold inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New submission
          </Link>
        </div>

        <div className="mb-8">
          <FestivalCredentialCard user={user} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <div data-testid="stat-films" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Films</div>
            <div className="text-3xl font-black">{subs.length}</div>
          </div>
          <div data-testid="stat-views" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Views</div>
            <div className="text-3xl font-black">{totalViews}</div>
          </div>
          <div data-testid="stat-votes" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Votes</div>
            <div className="text-3xl font-black">{totalVotes}</div>
          </div>
          <div data-testid="stat-avg" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Avg rating</div>
            <div className="text-3xl font-black flex items-center gap-2">{avgRating.toFixed(2)} <Star className="w-5 h-5 fill-amber-400 text-amber-400" /></div>
          </div>
        </div>

        {/* My Laurels — tier badges + Official Selections, share-ready */}
        {laurels.length > 0 && (
          <div data-testid="laurels-section" className="bg-gradient-to-br from-amber-900/15 to-[#0f0f0f] border border-amber-500/30 rounded-lg p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🏆</span>
              <div>
                <h3 className="text-lg font-black">Your laurels — share to socials</h3>
                <p className="text-xs text-gray-400">Auto-generated badges for jury tiers + Official Selections.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {laurels.map((l) => (
                <div key={l.submission_id + "-" + l.badge_kind}
                  data-testid={`laurel-card-${l.submission_id}`}
                  className="bg-black/40 border border-amber-500/20 rounded-lg overflow-hidden flex flex-col">
                  <img src={l.image_url} alt={l.title} className="w-full aspect-[2/3] object-cover" />
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                      {l.badge_kind.replace(/_/g, " ")}
                    </div>
                    <div className="text-sm font-bold text-white truncate">{l.title}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-auto pt-2">
                      <a
                        href={l.tweet_intent_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`share-x-${l.submission_id}`}
                        className="inline-flex items-center gap-1.5 bg-black hover:bg-zinc-900 border border-white/15 text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors"
                      >
                        𝕏 Share
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(l.instagram_caption); toast.success("Instagram caption copied"); }}
                        data-testid={`copy-ig-${l.submission_id}`}
                        className="inline-flex items-center gap-1.5 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border border-fuchsia-500/30 text-fuchsia-200 text-xs font-semibold px-3 py-1.5 rounded transition-colors"
                      >
                        📋 IG caption
                      </button>
                      <a
                        href={l.image_url}
                        download
                        data-testid={`download-laurel-${l.submission_id}`}
                        className="inline-flex items-center gap-1.5 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 text-xs font-semibold px-3 py-1.5 rounded transition-colors"
                      >
                        ⬇ Download
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payouts (Stripe Connect) */}
        <div data-testid="payouts-section" className="bg-gradient-to-br from-emerald-900/15 to-[#0f0f0f] border border-emerald-500/25 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="p-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
              <Wallet className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <h2 className="text-lg font-bold mb-1">Filmmaker payouts</h2>
              {connect?.connect_disabled ? (
                <p className="text-sm text-gray-400 leading-relaxed">
                  Stripe Connect is not yet activated on this deployment. Once the platform admin sets a real <code className="text-gray-300">STRIPE_API_KEY</code>, you'll be able to onboard your bank and earn 70% of every ticket sold (CineCinema retains 30%).
                </p>
              ) : connect?.onboarded ? (
                <p className="text-sm text-emerald-300 leading-relaxed flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Connected. Payouts route directly to your bank — 70% per ticket, paid out by Stripe.
                </p>
              ) : connect?.account_id ? (
                <p className="text-sm text-amber-300 leading-relaxed flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Onboarding incomplete — finish your Stripe setup to start receiving payouts.
                </p>
              ) : (
                <p className="text-sm text-gray-400 leading-relaxed">
                  Onboard with Stripe (takes 2 min) and earn <strong className="text-emerald-300">70%</strong> of every ticket sold for your films. Payouts route automatically to your bank.
                </p>
              )}
              {earnings && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div data-testid="earnings-tickets" className="bg-black/40 rounded p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Tickets sold</div>
                    <div className="text-xl font-black mt-1">{earnings.totals.tickets_sold}</div>
                  </div>
                  <div data-testid="earnings-gross" className="bg-black/40 rounded p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Gross revenue</div>
                    <div className="text-xl font-black mt-1">${earnings.totals.gross_revenue.toFixed(2)}</div>
                  </div>
                  <div data-testid="earnings-payout" className="bg-black/40 rounded p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Your payout (70%)</div>
                    <div className="text-xl font-black mt-1 text-emerald-400">${earnings.totals.filmmaker_payout.toFixed(2)}</div>
                  </div>
                  <div data-testid="earnings-platform" className="bg-black/40 rounded p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Platform fee (30%)</div>
                    <div className="text-xl font-black mt-1 text-gray-400">${earnings.totals.platform_fee.toFixed(2)}</div>
                  </div>
                </div>
              )}
            </div>
            {!connect?.connect_disabled && (
              connect?.onboarded ? (
                <button data-testid="connect-dashboard-btn" onClick={openDashboard} disabled={busy} className="btn-glass px-4 py-2 rounded font-semibold inline-flex items-center gap-2 border border-white/15">
                  Stripe dashboard <ExternalLink className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button data-testid="connect-onboard-btn" onClick={onboard} disabled={busy} className="btn-red px-5 py-2.5 rounded font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                  <Wallet className="w-4 h-4" /> {connect?.account_id ? "Resume onboarding" : "Onboard with Stripe"}
                </button>
              )
            )}
          </div>
        </div>

        {subs.length === 0 ? (
          <div className="text-center py-20 text-gray-500 bg-[#0f0f0f] border border-dashed border-white/10 rounded-lg">
            <Film className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="mb-4">You haven't submitted any films yet.</p>
            <Link to="/festival/submit" className="btn-red px-6 py-2.5 rounded font-semibold inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Submit your first film
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.submission_id} data-testid={`mine-${s.submission_id}`} className="flex items-center gap-4 bg-[#141414] border border-white/5 rounded-lg p-4">
                {s.poster_url ? (
                  <img src={s.poster_url} alt="" className="w-14 h-20 object-cover rounded flex-shrink-0" />
                ) : (
                  <div className="w-14 h-20 rounded flex items-center justify-center bg-black/40 flex-shrink-0"><Film className="w-6 h-6 text-gray-600" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/festival/films/${s.submission_id}`} className="font-bold text-lg hover:text-[#E50914] truncate block">{s.title}</Link>
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{s.region} · {s.runtime_min} min</span>
                    {s.my_role && s.my_role !== "owner" && (
                      <span className="text-[10px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5">
                        as {s.my_role}
                      </span>
                    )}
                    <span className={`uppercase tracking-widest font-bold ${
                      s.status === "approved" ? "text-green-400" :
                      s.status === "rejected" ? "text-red-400" :
                      s.status === "flagged" ? "text-amber-400" :
                      "text-yellow-400"
                    }`}>{s.status}</span>
                    {s.superseded_by && (
                      <span className="text-[10px] uppercase tracking-widest text-gray-600 bg-white/5 border border-white/10 rounded px-1.5 py-0.5">
                        superseded
                      </span>
                    )}
                  </div>
                  {s.status === "rejected" && s.rejected_reason && (
                    <div className="mt-1.5 text-[11px] text-red-300/80 italic line-clamp-1" title={s.rejected_reason}>
                      "{s.rejected_reason}"
                    </div>
                  )}
                  {s.status === "pending_payment" && !s.submission_fee_paid && !s.submission_fee_waived && (
                    <div className="mt-2 inline-flex items-center gap-2 text-[11px] bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1">
                      <DollarSign className="w-3 h-3 text-amber-400" />
                      <span className="text-amber-300 font-semibold">
                        Submission fee due: ${s.submission_fee_amount?.toFixed(2)} {(s.submission_fee_currency || "usd").toUpperCase()}
                      </span>
                      <span className="text-gray-500">({s.submission_fee_tier})</span>
                    </div>
                  )}
                  {s.subtitles_url && (
                    <div className="mt-2 inline-flex flex-wrap items-center gap-2 text-[11px] bg-blue-500/10 border border-blue-500/30 rounded px-2 py-1" title={`Auto-generated on ${s.ai_transcribed_at ? new Date(s.ai_transcribed_at).toLocaleDateString() : "upload"}`}>
                      <span className="text-blue-300 font-semibold">🔤 Auto-subtitles ready</span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/festival/submissions/${s.submission_id}/subtitles.srt`}
                        data-testid={`download-srt-${s.submission_id}`}
                        className="text-blue-200 hover:text-white underline underline-offset-2"
                        download
                        onClick={(e) => e.stopPropagation()}
                      >
                        Download .srt
                      </a>
                      {subtitleLangs.length > 0 && (
                        <select
                          data-testid={`translate-srt-${s.submission_id}`}
                          disabled={translateBusy === s.submission_id}
                          defaultValue=""
                          onChange={(e) => {
                            const t = e.target.value;
                            e.target.value = "";
                            if (t) translateSubtitles(s.submission_id, t);
                          }}
                          className="bg-black/40 border border-blue-500/40 rounded px-1.5 py-0.5 text-[11px] text-blue-100 hover:border-blue-400 focus:outline-none focus:border-blue-300 disabled:opacity-50 cursor-pointer"
                          title="Translate to another language"
                        >
                          <option value="" disabled>
                            {translateBusy === s.submission_id ? "Translating…" : "Translate ▾"}
                          </option>
                          {subtitleLangs.map((l) => (
                            <option key={l.code} value={l.code}>{l.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <div className="text-gray-500 text-[10px] uppercase tracking-widest">Views</div>
                    <div className="font-bold flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{s.view_count || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500 text-[10px] uppercase tracking-widest">Votes</div>
                    <div className="font-bold">{s.total_votes || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500 text-[10px] uppercase tracking-widest">Avg</div>
                    <div className="font-bold flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{(s.avg_rating || 0).toFixed(2)}</div>
                  </div>
                </div>
                {s.my_role === "owner" && s.status === "pending_payment" && !s.submission_fee_paid && !s.submission_fee_waived && (
                  <button
                    data-testid={`pay-fee-${s.submission_id}`}
                    onClick={() => payFee(s)}
                    disabled={feeBusy === s.submission_id}
                    className="bg-gradient-to-br from-amber-500 to-[#E50914] hover:brightness-110 text-white px-3 py-1.5 rounded text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {feeBusy === s.submission_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
                    Pay ${s.submission_fee_amount?.toFixed(2)}
                  </button>
                )}
                {s.my_role === "owner" && (s.status === "rejected" || s.status === "flagged") && !s.superseded_by && (
                  <Link
                    data-testid={`resubmit-${s.submission_id}`}
                    to={`/festival/submit?clone=${s.submission_id}`}
                    className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Edit & resubmit
                  </Link>
                )}
                {s.my_role === "owner" && (
                  <button data-testid={`delete-${s.submission_id}`} onClick={() => del(s.submission_id)} className="text-gray-500 hover:text-red-500 p-2" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}      </main>
    </div>
  );
}
